import React from 'react';
import { CheckCircle, Heart, Users, Target, Lightbulb } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
export function AboutPage() {
  const values = [{
    icon: Heart,
    title: 'Compassion',
    description: 'We serve humanity with empathy and kindness, ensuring that healthcare reaches those who need it most, regardless of background.'
  }, {
    icon: CheckCircle,
    title: 'Integrity',
    description: 'We uphold the highest ethical standards in medical practice, maintaining transparency and accountability in all our endeavors.'
  }, {
    icon: Users,
    title: 'Unity',
    description: 'We foster a supportive community that brings together medical professionals from diverse specialties and experiences.'
  }, {
    icon: Target,
    title: 'Excellence',
    description: 'We strive for the highest quality in medical practice, education, and humanitarian service.'
  }];
  return <>
      <SEO title="About Us" description="Learn about Khyber Medical Association UK - our mission, values, and commitment to medical excellence and humanitarian service." data-id="element-123" />

      {/* Hero */}
      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-20 md:py-28" data-id="element-124">
        <div className="container mx-auto px-4 md:px-6" data-id="element-125">
          <FadeIn className="max-w-3xl mx-auto text-center" data-id="element-126">
            <img src="https://cdn.magicpatterns.com/uploads/stkewdFoDLsveAsLMC6296/favi__logo.png" alt="KMA UK Logo" className="h-24 w-24 mx-auto mb-6 object-contain brightness-0 invert opacity-90" data-id="element-127" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-128">
              About KMA UK
            </h1>
            <p className="text-xl text-primary-50 leading-relaxed" data-id="element-129">
              A trusted community of medical professionals united by shared
              values, dedicated to excellence in healthcare and service to
              humanity.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Who We Are */}
      <Section data-id="element-130">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-131">
          <FadeIn data-id="element-132">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-6" data-id="element-133">
              Who We Are
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed space-y-4" data-id="element-134">
              <p data-id="element-135">
                The Khyber Medical Association UK is a professional and alumni
                organization that brings together medical graduates from Khyber
                Medical College and other healthcare professionals of Khyber
                Pakhtunkhwa origin residing in the United Kingdom.
              </p>
              <p data-id="element-136">
                Established by a group of visionary doctors who recognized the
                need for a unified platform, we have grown into a respected
                organization known for our commitment to medical ethics,
                professional development, and humanitarian service.
              </p>
              <p data-id="element-137">
                Our members include consultants, general practitioners, junior
                doctors, medical students, and allied health professionals, all
                connected by their roots in KPK and their dedication to making a
                positive impact in healthcare.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-138">
            <div className="relative" data-id="element-139">
              <div className="absolute -inset-4 bg-primary-50 rounded-2xl transform rotate-3" data-id="element-140" />
              <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=2091&q=80" alt="Asian medical professionals at networking event" className="relative rounded-xl shadow-xl w-full" data-id="element-141" />
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Important Statement */}
      <Section background="accent" data-id="element-142">
        <FadeIn data-id="element-143">
          <div className="max-w-4xl mx-auto" data-id="element-144">
            <div className="bg-white border-l-4 border-accent-700 p-8 rounded-r-xl shadow-md" data-id="element-145">
              <div className="flex items-start gap-4" data-id="element-146">
                <div className="flex-shrink-0" data-id="element-147">
                  <div className="h-12 w-12 rounded-full bg-accent-100 flex items-center justify-center" data-id="element-148">
                    <CheckCircle className="h-6 w-6 text-accent-700" data-id="element-149" />
                  </div>
                </div>
                <div data-id="element-150">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-151">
                    Our Independence
                  </h3>
                  <p className="text-gray-700 text-lg leading-relaxed" data-id="element-152">
                    The Khyber Medical Association UK does not associate itself
                    with any political, ethnic, or religious organisations. We
                    are an independent professional body focused solely on
                    healthcare excellence, education, and humanitarian service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* Our Purpose */}
      <Section data-id="element-153">
        <div className="max-w-4xl mx-auto" data-id="element-154">
          <FadeIn className="text-center mb-12" data-id="element-155">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-6" data-id="element-156">
              Our Purpose
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-157">
              We exist to strengthen the bonds among medical professionals while
              extending our expertise and resources to serve communities in
              need.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-158">
            {[{
            title: 'Professional Networking',
            description: "Creating opportunities for medical professionals to connect, collaborate, and support each other's career development."
          }, {
            title: 'Alumni Connection',
            description: 'Maintaining strong ties with Khyber Medical College and fostering lifelong relationships among graduates.'
          }, {
            title: 'Humanitarian Relief',
            description: 'Providing medical aid, disaster relief, and healthcare support to underserved communities in Pakistan.'
          }, {
            title: 'Medical Education',
            description: 'Supporting the next generation through scholarships, mentorship programs, and educational initiatives.'
          }, {
            title: 'Community Engagement',
            description: 'Organizing events, seminars, and social gatherings that strengthen our community bonds.'
          }, {
            title: 'Medical Ethics',
            description: 'Upholding the highest standards of medical practice and promoting ethical healthcare delivery.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-159">
                <Card className="h-full border-primary-100 hover:shadow-md transition-shadow" data-id="element-160">
                  <CardContent className="p-6" data-id="element-161">
                    <h3 className="text-lg font-bold text-primary-900 mb-2" data-id="element-162">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-163">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Our Values */}
      <Section background="gray" data-id="element-164">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-165">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-166">
            Our Values
          </h2>
          <p className="text-gray-600" data-id="element-167">
            These core principles guide everything we do, from professional
            practice to charitable work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-168">
          {values.map((value, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-169">
              <Card className="h-full text-center border-none shadow-lg hover:shadow-xl transition-shadow" data-id="element-170">
                <CardHeader className="pb-4" data-id="element-171">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto" data-id="element-172">
                    <value.icon className="h-8 w-8" data-id="element-173" />
                  </div>
                  <CardTitle className="text-xl" data-id="element-174">{value.title}</CardTitle>
                </CardHeader>
                <CardContent data-id="element-175">
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-176">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Our Future Vision */}
      <Section data-id="element-177">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-178">
          <FadeIn data-id="element-179">
            <img src="https://images.unsplash.com/photo-1551076805-e1869033e561?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Healthcare professionals planning" className="rounded-xl shadow-xl w-full" data-id="element-180" />
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-181">
            <div className="flex items-start gap-4 mb-6" data-id="element-182">
              <div className="flex-shrink-0" data-id="element-183">
                <div className="h-12 w-12 rounded-full bg-accent-100 flex items-center justify-center" data-id="element-184">
                  <Lightbulb className="h-6 w-6 text-accent-700" data-id="element-185" />
                </div>
              </div>
              <div data-id="element-186">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-187">
                  Our Future Vision
                </h2>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed space-y-4" data-id="element-188">
              <p data-id="element-189">
                We envision a future where KMA UK serves as a model for
                professional medical associations worldwide—one that seamlessly
                blends career advancement with meaningful humanitarian impact.
              </p>
              <p data-id="element-190">
                Our goals include expanding our charitable reach to more
                communities in Pakistan, establishing sustainable healthcare
                programs, creating more opportunities for young doctors through
                mentorship and scholarships, and strengthening the global
                network of KPK medical professionals.
              </p>
              <p data-id="element-191">
                We are committed to leveraging technology to connect our members
                across continents, facilitating knowledge sharing, and ensuring
                that distance never diminishes our collective impact.
              </p>
              <p className="font-medium text-primary-800" data-id="element-192">
                Together, we are building a legacy of medical excellence and
                compassionate service that will benefit generations to come.
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* History Timeline */}
      <Section background="gray" data-id="element-193">
        <div className="max-w-3xl mx-auto" data-id="element-194">
          <div className="text-center mb-12" data-id="element-195">
            <h2 className="text-3xl font-serif font-bold text-primary-900 mb-4" data-id="element-196">
              Our Journey
            </h2>
            <p className="text-gray-600" data-id="element-197">
              Key milestones in our history of service and dedication.
            </p>
          </div>

          <div className="space-y-12 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-primary-300 before:to-transparent" data-id="element-198">
            {[{
            year: '1985',
            title: 'Foundation',
            desc: 'KMA UK was established by a group of visionary doctors committed to professional excellence.'
          }, {
            year: '1995',
            title: 'Charitable Status',
            desc: 'Officially registered as a charity in England & Wales, expanding our humanitarian mission.'
          }, {
            year: '2005',
            title: 'Earthquake Relief',
            desc: 'Major humanitarian mission deployed to Northern Pakistan following devastating earthquake.'
          }, {
            year: '2010',
            title: 'Education Fund',
            desc: 'Launched scholarship program for deserving medical students from KPK.'
          }, {
            year: '2022',
            title: 'Flood Response',
            desc: 'Coordinated emergency medical relief for flood-affected regions across Pakistan.'
          }, {
            year: '2024',
            title: 'Digital Era',
            desc: 'Launching digital initiatives to connect the next generation of medical professionals.'
          }].map((item, i) => <FadeIn key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-199">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-primary-600 shadow-lg shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-200">
                  <div className="w-3 h-3 bg-white rounded-full" data-id="element-201" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-202">
                  <div className="flex items-center justify-between space-x-2 mb-2" data-id="element-203">
                    <h3 className="font-bold text-gray-900 text-lg" data-id="element-204">
                      {item.title}
                    </h3>
                    <time className="font-serif font-bold text-accent-600 text-lg" data-id="element-205">
                      {item.year}
                    </time>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-206">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>
    </>;
}